<template>
  <div>
    <div class="d-flex mb-4">
      <h5>{{ appTranslate('Version history') }}</h5>

      <router-link class="ml-auto mr-4" :title="appTranslate('Advanced comparison')" :to="{ name: 'product-comparison' }">
        {{ appTranslate('Advanced comparison') }}
        <i class="fa fa-external-link" />
      </router-link>
    </div>

    <div class="table-data" :class="{ comparing: comparedRow !== undefined }">
      <!-- Headers -->
      <div class="d-flex font-weight-bold mb-2">
        <div class="column">{{ appTranslate('Date changed') }}</div>
        <div class="column">{{ appTranslate('Changed by') }}</div>
        <div class="column">Changes</div>
      </div>

      <!-- Data -->
      <div v-for="(version, i) of versionOptions" :key="i" class="table-row" :class="{ 'compared-row': comparedRow === i }">
        <div class="d-flex w-100">
          <div class="column">
            <a class="cursor-pointer" @click="viewVersion(version.value)">
              {{ version.displayValue }}
            </a>
          </div>
          <div class="column">
            {{ version.user }}
          </div>
          <div v-if="i !== versionOptions.length - 1" class="column d-flex align-items-center">
            <LoaderComponent v-if="loadingRow === i" static size="small" inner class="ml-1" />
            <a v-else class="cursor-pointer" @click="toggleComparison(i)">
              {{ appTranslate(comparedRow === i ? 'Hide changes' : 'See changes') }}
            </a>
          </div>
        </div>

        <div v-if="comparedRow === i && comparisons" class="mt-3">
          <VersionChangesComponent :comparisons="comparisons" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from '@/composables/useTranslation'
import { SimplifiedProduct } from '@/models/Product'
import { Comparison, VersionOption } from '@/models/Property'
import DataService from '@/services/DataService'
import { arrayToRecord } from '@/utils/tools'
import { computed, nextTick, ref } from 'vue'
import VersionChangesComponent from './VersionChangesComponent.vue'
import LoaderComponent from '../simple/LoaderComponent.vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{ product: SimplifiedProduct }>()
const emit = defineEmits<{ 'view-version': [version?: string] }>()

const { appTranslate } = useTranslation()

// -------------------
// -- Reactive vars --
// -------------------
const comparisons = ref<Comparison[]>()
const comparedRow = ref<number>()
const loadingRow = ref<number>()

// --------------------
// -- Computed props --
// --------------------
const indexedEdited = computed(() =>
  props.product.edited ? arrayToRecord(props.product.edited, 'time', (key) => String(key).replaceAll(':', '.')) : undefined,
)

const versionOptions = computed(() => {
  const arr = []
  const timestamp = props.product?.edited?.[props.product.edited.length - 1]?.time

  if (timestamp) {
    const date = new Date(timestamp)
    const key = date.toISOString().replaceAll(':', '.')
    arr.push({ value: undefined, displayValue: `${date.toLocaleString()} (${appTranslate('Current version')})`, user: indexedEdited.value?.[key]?.user })
  }

  props.product.versions?.forEach((version) => {
    arr.push({ ...version, user: indexedEdited.value?.[version.value]?.user })
  })

  return arr
})

const reversedProductEdited = computed(() => (props.product.edited ? Array.from(props.product.edited)?.reverse() : undefined))

// ---------------
// -- Functions --
// ---------------
const viewVersion = (version?: string) => {
  emit('view-version', version)
}

const toggleComparison = (i: number) => {
  if (comparisons.value && comparedRow.value === i) {
    comparisons.value = undefined
    comparedRow.value = undefined
  } else {
    compareVersions(i, versionOptions.value[i], versionOptions.value[i + 1])
  }
}

const compareVersions = async (row: number, compareBasis?: VersionOption, compareTo?: VersionOption) => {
  loadingRow.value = row
  // comparisons.value = undefined

  const compareBasisVersion = compareBasis?.value ? await DataService.getProductVersion(props.product.sku, compareBasis.value) : undefined
  const compareToVersion = compareTo?.value ? await DataService.getProductVersion(props.product.sku, compareTo.value) : undefined

  comparisons.value = [
    {
      ...(compareTo && compareToVersion && { version: { ...compareTo, productInput: compareToVersion } }),
    },
    {
      product: compareBasisVersion ? undefined : props.product,
      ...(compareBasis && compareBasisVersion && { version: { ...compareBasis, productInput: compareBasisVersion } }),
    },
  ]
  loadingRow.value = undefined
  comparedRow.value = row
}
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.table-row {
  display: flex;
  flex-wrap: wrap;
  margin: 0.25em 0;
  padding: 0.5em;
}

.compared-row {
  border: 4px solid #ddd;
  // box-shadow: 0 0 1em #0005;
  margin: -5px;
}

.column {
  // flex: 1 0 0;
  flex: 0 1 33%;
  // padding: 2px 16px 2px 1px;
}
</style>
